/**
 * @file index.tsx
 *
 * @fileoverview Shows a contact request form.
 */
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Resume } from '~/jobiqo/types/types';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Textarea } from '~/vcl/atoms/01-forms/04-textarea';
import SubmitButton from '~/jobiqo/components/Button/Submit';

interface ContactRequestFormProps {
  /**
   * The resume.
   */
  resume: Resume;
  /**
   * when form finishes sending.
   */
  onComplete: (event: React.FormEvent<HTMLFormElement>) => void;
  /**
   * List of errors when submitting the form.
   */
  ErrorList?: React.ReactNode;
  /**
   * If the mutation is being triggered (loading).
   */
  isSubmitting: boolean;
}

export function ContactRequestForm({
  resume,
  onComplete,
  ErrorList,
  isSubmitting,
}: ContactRequestFormProps) {
  const [message, setMessage] = useState<string>('');

  const intl = useIntl();

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onComplete(event);
  }

  return (
    <>
      <h2>
        <FormattedMessage
          id="contact_request_form_title"
          defaultMessage="Send contact request"
        />
      </h2>
      <Spacing size="medium" />
      {ErrorList && (
        <>
          {ErrorList}
          <Spacing />
        </>
      )}
      <form onSubmit={handleSubmit}>
        <Textarea
          type="text"
          rows={10}
          label={intl.formatMessage({
            id: 'contact_request_body_label',
            defaultMessage: 'Contact message',
          })}
          placeholder={intl.formatMessage({
            id: 'contact_request_body',
            defaultMessage: 'Enter your message',
          })}
          description={intl.formatMessage({
            id: 'contact_request_body_desc',
            defaultMessage:
              'Message is optional and if filled this will be sent to the applicant along with the request.',
          })}
          value={message}
          onChange={({ target: { value } }) => setMessage(value)}
          name="message"
        />
        <Spacing />
        <SubmitButton
          isSubmitting={isSubmitting}
          bordered={true}
          buttonStyle="secondary"
          data-testid={`jbq-contact-request-submit-${resume.id}`}
        >
          <FormattedMessage
            id="contact_request_button_create"
            defaultMessage="Send contact request"
          />
        </SubmitButton>
      </form>
    </>
  );
}
export default ContactRequestForm;
